import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { emptyCv } from "@/lib/cv";
import CvForm from "@/components/CvForm";
import TramTemplate from "@/components/TramTemplate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Download, Save, ArrowLeft, Share2, Palette, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_PRESETS = [
  { id: "__hospitality", name: "Hospitality (Gold)", accent_color: "#C5A880", tagline: "" },
  { id: "__corporate", name: "Corporate (Navy)", accent_color: "#0F1C3F", tagline: "" },
  { id: "__wine", name: "Wine & Estate (Burgundy)", accent_color: "#7A1F2B", tagline: "" },
  { id: "__creative", name: "Creative (Copper)", accent_color: "#B0592F", tagline: "" },
];

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cv, setCv] = useState(emptyCv());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEnabled, setShareEnabled] = useState(false);
  const [shareToken, setShareToken] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [userPresets, setUserPresets] = useState([]);
  const previewRef = useRef(null);

  useEffect(() => {
    api
      .get(`/cv/${id}`)
      .then((r) => {
        const data = { ...emptyCv(), ...r.data.data };
        // ensure preset object exists
        data.preset = data.preset || { accent_color: "", tagline: "" };
        setCv(data);
        setShareEnabled(!!r.data.share_enabled);
        setShareToken(r.data.share_token || "");
      })
      .catch(() => toast.error("CV not found"))
      .finally(() => setLoading(false));
    api.get("/presets").then((r) => setUserPresets(r.data.items || [])).catch(() => {});
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/cv", { id, data: cv });
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    if (!previewRef.current) return;
    setExporting(true);
    const el = previewRef.current;
    const prevTransform = el.style.transform;
    try {
      await api.post("/cv", { id, data: cv });
      el.style.transform = "none";
      el.classList.add("pdf-mode");
      const html2pdf = (await import("html2pdf.js")).default;
      const opt = {
        margin: 0,
        filename: `${(cv.full_name || "TRAM_CV").replace(/\s+/g, "_")}_TRAM.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          windowWidth: 794,
        },
        jsPDF: { unit: "px", format: [794, 1123], orientation: "portrait", hotfixes: ["px_scaling"] },
        pagebreak: { mode: ["css"] },
      };
      await html2pdf().set(opt).from(el).save();
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      el.style.transform = prevTransform;
      el.classList.remove("pdf-mode");
      setExporting(false);
    }
  };

  const shareUrl = shareToken
    ? `${window.location.origin}/share/${shareToken}`
    : "";

  const toggleShare = async (on) => {
    setShareBusy(true);
    try {
      if (on) {
        await api.post("/cv", { id, data: cv });
        const r = await api.post(`/cv/${id}/share`);
        setShareToken(r.data.share_token);
        setShareEnabled(true);
        toast.success("Share link enabled");
      } else {
        await api.delete(`/cv/${id}/share`);
        setShareEnabled(false);
        toast.success("Share link revoked");
      }
    } catch {
      toast.error("Share update failed");
    } finally {
      setShareBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const applyPreset = (preset) => {
    setCv((c) => ({
      ...c,
      preset: { accent_color: preset.accent_color || "", tagline: preset.tagline || "" },
    }));
    toast.success(`Preset applied · ${preset.name}`);
  };

  const clearPreset = () => {
    setCv((c) => ({ ...c, preset: { accent_color: "", tagline: "" } }));
    toast.success("Preset cleared");
  };

  const savePreset = async () => {
    if (!presetName.trim()) return toast.error("Give the preset a name");
    try {
      const r = await api.post("/presets", {
        name: presetName.trim(),
        accent_color: cv.preset?.accent_color || "",
        tagline: cv.preset?.tagline || "",
      });
      setUserPresets((p) => [...p, r.data]);
      setPresetName("");
      setPresetOpen(false);
      toast.success("Preset saved");
    } catch {
      toast.error("Could not save preset");
    }
  };

  const deletePreset = async (pid) => {
    try {
      await api.delete(`/presets/${pid}`);
      setUserPresets((p) => p.filter((x) => x.id !== pid));
      toast.success("Preset deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return <div className="text-[#5a6578]">Loading editor…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => nav("/library")}
            className="text-[#0f1c3f] hover:opacity-80 flex items-center gap-1 text-sm"
            data-testid="back-to-library"
          >
            <ArrowLeft size={14} /> Library
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-[#cfc8ba]" data-testid="preset-btn">
                <Palette size={14} className="mr-1.5" /> Template
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="overline">Sector presets</DropdownMenuLabel>
              {DEFAULT_PRESETS.map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  onClick={() => applyPreset(p)}
                  data-testid={`preset-${p.id}`}
                >
                  <span
                    className="inline-block w-3 h-3 rounded-full mr-2 border border-[#cfc8ba]"
                    style={{ backgroundColor: p.accent_color }}
                  />
                  {p.name}
                </DropdownMenuItem>
              ))}
              {userPresets.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="overline">Your presets</DropdownMenuLabel>
                  {userPresets.map((p) => (
                    <DropdownMenuItem
                      key={p.id}
                      onClick={() => applyPreset(p)}
                      className="flex items-center justify-between"
                    >
                      <span className="flex items-center">
                        <span
                          className="inline-block w-3 h-3 rounded-full mr-2 border border-[#cfc8ba]"
                          style={{ backgroundColor: p.accent_color || "#0f1c3f" }}
                        />
                        {p.name}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deletePreset(p.id);
                        }}
                        className="text-[#c62828]"
                      >
                        <Trash2 size={12} />
                      </button>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={clearPreset} data-testid="preset-clear">
                Reset to default
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPresetOpen(true)} data-testid="preset-save-open">
                Save current as preset…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="outline"
            onClick={() => setShareOpen(true)}
            className="border-[#cfc8ba]"
            data-testid="share-btn"
          >
            <Share2 size={14} className="mr-1.5" /> Share
          </Button>
          <Button
            variant="outline"
            onClick={save}
            disabled={saving}
            className="border-[#cfc8ba]"
            data-testid="save-cv-btn"
          >
            <Save size={14} className="mr-1.5" />
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            onClick={download}
            disabled={exporting}
            className="bg-[#0f1c3f] hover:bg-[#0a122a] text-white"
            data-testid="download-pdf-btn"
          >
            <Download size={14} className="mr-1.5" />
            {exporting ? "Exporting…" : "Download PDF"}
          </Button>
        </div>
      </div>

      {/* Accent color inline picker */}
      <div className="mb-4 flex items-center gap-3 text-sm text-[#5a6578]">
        <Label className="overline m-0">Accent</Label>
        <input
          type="color"
          value={cv.preset?.accent_color || "#0f1c3f"}
          onChange={(e) =>
            setCv((c) => ({
              ...c,
              preset: { ...(c.preset || {}), accent_color: e.target.value },
            }))
          }
          className="w-8 h-8 rounded-md border border-[#cfc8ba] bg-white cursor-pointer"
          data-testid="accent-color-input"
        />
        <Input
          placeholder="Custom band tagline (optional)"
          value={cv.preset?.tagline || ""}
          onChange={(e) =>
            setCv((c) => ({
              ...c,
              preset: { ...(c.preset || {}), tagline: e.target.value },
            }))
          }
          className="max-w-sm bg-white border-[#cfc8ba] h-9"
          data-testid="tagline-input"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="tram-scroll overflow-y-auto max-h-[calc(100vh-220px)] pr-2">
          <CvForm cv={cv} setCv={setCv} />
        </div>
        <div
          className="tram-scroll overflow-auto max-h-[calc(100vh-220px)] bg-[#f5f2ec] rounded-lg p-4"
          data-testid="preview-pane"
        >
          <div ref={previewRef} className="cv-preview-scale">
            <TramTemplate cv={cv} />
          </div>
        </div>
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#0f1c3f]">
              Share this CV
            </DialogTitle>
            <DialogDescription>
              Enable a private link to send this CV to a client — they can view and print
              without signing in. Revoke it any time.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between border border-[#e2ddd3] rounded-md p-3 bg-[#faf8f5]">
            <div>
              <div className="text-sm font-semibold text-[#0f1c3f]">Share link</div>
              <div className="text-xs text-[#7a6b5d]">
                {shareEnabled ? "Anyone with the link can view this CV." : "Disabled."}
              </div>
            </div>
            <Switch
              checked={shareEnabled}
              onCheckedChange={toggleShare}
              disabled={shareBusy}
              data-testid="share-toggle"
            />
          </div>
          {shareEnabled && shareUrl && (
            <div className="mt-3">
              <Label className="overline">Public URL</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input value={shareUrl} readOnly className="bg-white border-[#cfc8ba]" data-testid="share-url" />
                <Button
                  onClick={copyLink}
                  variant="outline"
                  className="border-[#cfc8ba]"
                  data-testid="copy-share-link"
                >
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
              <p className="text-xs text-[#7a6b5d] mt-2">
                Saving new edits to this CV will be visible on the public link automatically.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShareOpen(false)}
              className="border-[#cfc8ba]"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#0f1c3f]">
              Save template preset
            </DialogTitle>
            <DialogDescription>
              Save the current accent colour and tagline for reuse on future CVs.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="overline">Preset name</Label>
            <Input
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="e.g. Hospitality UHNW"
              className="mt-1 bg-white border-[#cfc8ba]"
              data-testid="preset-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPresetOpen(false)} className="border-[#cfc8ba]">
              Cancel
            </Button>
            <Button onClick={savePreset} className="bg-[#0f1c3f] hover:bg-[#0a122a] text-white" data-testid="preset-save-btn">
              Save preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
